import keyBy from "lodash.keyby";
import type { RadioChangeEvent } from "antd";

import { W1 } from "@components/Constants";
import { US_STATE_OPTIONS } from "@components/Address";
import { Disclaimer, MarkdownAlert } from "@components/Text";
import { INVESTMENT_PROFILE_TYPE_SDIRA } from "@components/Domain";
import {
  TYPE_EIN,
  TYPE_ZIP,
  TYPE_RADIO,
  TYPE_HIDDEN,
  TYPE_STRING,
  TYPE_BOOLEAN,
} from "@components/Form";
import { listCustodiansOperation } from "@api/services/investments";
import {
  CUSTODIAN_REVIEWED_SDIRA,
  CHECKBOOK_CONTROLLED_SDIRA,
} from "@api/services/investments/shapes/SdiraTypeEnum";

import computeProfileInput from "./computeProfileInput";
import computeLiabilityInputs from "./computeLiabilityInputs";
import getProfileSdiraInputsMap from "./getProfileSdiraInputsMap";
import RULES from "./rules";
import type {
  Form,
  Input,
  Profile,
  Custodian,
  Organization,
  UpdateSchema,
  FormProfileSdira,
} from "../types";

const LABEL_INVALID_SDIRA_TYPE = "Invalid investment SDIRA type selected";

const CHECKBOOK_MESSAGE_ALERT = `
  Your selection indicates a custodian-reviewed SDIRA. In this setup, your
  custodian must review or sign off on your investment. If that is your
  structure, please return to the start and select “Custodian-reviewed SDIRA”
  instead, or seek professional advice before proceeding.
`;

const CUSTODIAN_MESSAGE_ALERT = `
  Your selection indicates a checkbook-controlled SDIRA. This setup typically
  invests through an IRA-owned single-member disregarded LLC or disregarded
  grantor trust. If that isn’t your structure, please return to the start and
  select “Checkbook-controlled SDIRA” instead, or seek professional advice
  before proceeding.
`;

const ACCOUNT_NAME_EXTRA = `
  Information entered in this field will be used as reference when your
  distribution is sent electronically via ACH to your custodian. Please state
  your custodian's name, your name, and your account number in this format:
  Custodian Name FBO Your Name Account Number (e.g., "IRA Financial FBO John
  Doe 198204")
`;

const SDIRA_TYPE_EXTRA = `
  A custodian-reviewed SDIRA requires your custodian to review and sign
  investment documents. A checkbook-controlled SDIRA invests through an
  IRA-owned single-member LLC or grantor trust, giving you direct control over
  investment decisions. If you are not sure which option applies to you, please
  seek professional advice before proceeding.
`;

const ERROR_VALIDATION_ACCOUNT_NAME_MESSAGE = "Should not exceed 80 characters";

const ACCOUNT_NAME_MAX_LENGTH = 120;
const ENTITY_NAME_MAX_LENGTH = 255;

const CUSTODIAN_OTHER_ID = "OTHER";
const CUSTODIAN_OTHER_LABEL = "Other";

const CUSTODIANS_EXTRA_OPTIONS = [
  {
    id: CUSTODIAN_OTHER_ID,
    name: CUSTODIAN_OTHER_LABEL,
  },
];

const WARNING_STYLE = { marginTop: W1, marginBottom: 0 };
const REGION_INPUT_STYLE = { width: 170 };

const getProfileSdiraSchema = (
  organization: Organization,
  updateSchema: UpdateSchema,
  isEdit: boolean,
  profileNameExtraWarning: string | null,
  profile: FormProfileSdira = {},
  existingProfiles: Profile[] = [],
): Input[] => {
  const {
    custodianId: profileCustodianId,
    sdiraType,
    entityName,
    einNumber,
    hasEinNumber,
    profileName,
    accountName,
    shouldCustodianSign,
    isLiabilityAccepted,
    hasPurchasedExemptSecurities,
  } = profile;

  const inputPropsMap = getProfileSdiraInputsMap();

  const isCheckbookControlled = sdiraType === CHECKBOOK_CONTROLLED_SDIRA;

  const isCustodianOtherSelected = profileCustodianId === CUSTODIAN_OTHER_ID;

  const liabilityInputs = computeLiabilityInputs(updateSchema, organization);
  const profileSelectInput = computeProfileInput(INVESTMENT_PROFILE_TYPE_SDIRA, updateSchema, existingProfiles, () => ({}));

  const hasProfiles = existingProfiles
    .filter(({ disabled }) => !disabled).length > 0;

  const shouldHideAnotherFields =
    hasProfiles &&
    !profileName &&
    !isEdit;

  if (shouldHideAnotherFields) {
    return [
      ...profileSelectInput,
      inputPropsMap.isLiabilityAccepted,
      inputPropsMap.hasPurchasedExemptSecurities,
      inputPropsMap.membershipInterestsTitle,
      inputPropsMap.sdiraType,
      inputPropsMap.shouldCustodianSign,
      inputPropsMap.entityName,
      inputPropsMap.hasEinNumber,
      inputPropsMap.einNumber,
      inputPropsMap.custodianId,
      inputPropsMap.accountName,
      inputPropsMap.custodianName,
      inputPropsMap.custodianAddress,
      inputPropsMap.custodianEinNumber,
    ];
  }

  const getAccountNameExtra = () => {
    if (profileNameExtraWarning) {
      return (
        <>
          <Disclaimer text={ACCOUNT_NAME_EXTRA} />
          <MarkdownAlert
            text={profileNameExtraWarning}
            type="warning"
            style={WARNING_STYLE}
          />
        </>
      );
    }

    return <Disclaimer text={ACCOUNT_NAME_EXTRA} />;
  };

  const entityNameInput: Input = isCheckbookControlled
    ? {
      name: "entityName",
      type: TYPE_STRING,
      label: "Entity’s Full Legal Name",
      required: true,
      maxLength: ENTITY_NAME_MAX_LENGTH,
    }
    : inputPropsMap.entityName;

  const hasEinNumberInput: Input = isCheckbookControlled
    ? {
      name: "hasEinNumber",
      label: "Does this entity have an EIN?",
      required: true,
      inputProps: {
        onChange: () => updateSchema(),
      },
    }
    : inputPropsMap.hasEinNumber;

  const einNumberInput: Input = isCheckbookControlled && hasEinNumber
    ? {
      name: "einNumber",
      type: TYPE_EIN,
      label: "Entity EIN",
      required: true,
    }
    : inputPropsMap.einNumber;

  const isDisabledFields = !isCustodianOtherSelected;

  const updateAlert = (_sdiraType?: string, _shouldCustodianSign?: boolean) => {
    const hasSdiraType = _sdiraType !== undefined;
    const hasShouldSignValue = _shouldCustodianSign !== undefined;

    if (!hasSdiraType) {
      return updateSchema();
    }

    if (!hasShouldSignValue) {
      return updateSchema();
    }

    const isCheckbook = _sdiraType === CHECKBOOK_CONTROLLED_SDIRA;

    const isCheckbookShouldSign =
      isCheckbook &&
      _shouldCustodianSign;

    if (isCheckbookShouldSign) {
      return updateSchema(CHECKBOOK_MESSAGE_ALERT, "shouldCustodianSign");
    }

    const isCustodianShouldNotSign =
      !isCheckbook &&
      !_shouldCustodianSign;

    if (isCustodianShouldNotSign) {
      return updateSchema(CUSTODIAN_MESSAGE_ALERT, "shouldCustodianSign");
    }

    return updateSchema();
  };

  const onSdiraTypeChange = (event: RadioChangeEvent, form: Form) => {
    const { value: _sdiraType } = event.target;
    const { profileSdira = {} } = form.getFieldsValue();
    const { shouldCustodianSign: _shouldCustodianSign } = profileSdira;

    return updateAlert(_sdiraType, _shouldCustodianSign);
  };

  const onShouldCustodianSignChange = (event: RadioChangeEvent, form: Form) => {
    const { value: _shouldCustodianSign } = event.target;
    const { profileSdira = {} } = form.getFieldsValue();
    const { sdiraType: _sdiraType } = profileSdira;

    return updateAlert(_sdiraType, _shouldCustodianSign);
  };

  const onCustodianChange = (id: string | undefined, form: Form, options: Custodian[]) => {
    if (!id) {
      form.resetFields([ "profileSdira" ]);

      form.setFieldsValue({
        profileSdira: {
          sdiraType,
          entityName,
          hasEinNumber,
          einNumber,
          profileName,
          accountName,
          shouldCustodianSign,
          isLiabilityAccepted,
          hasPurchasedExemptSecurities,
        },
      });

      return updateSchema();
    }

    const custodianMaps: Record<string, Custodian> = keyBy([
      ...options, { id: CUSTODIAN_OTHER_ID, name: CUSTODIAN_OTHER_LABEL },
    ], "id");

    const custodian = custodianMaps[id];

    const {
      id: custodianId,
      name: custodianName,
      address: custodianAddress,
      einNumber: custodianEinNumber,
    } = custodian;

    const isOtherSelected = custodianId === CUSTODIAN_OTHER_ID;

    if (isOtherSelected) {
      form.resetFields([ "profileSdira" ]);

      form.setFieldsValue({
        profileSdira: {
          sdiraType,
          entityName,
          hasEinNumber,
          einNumber,
          custodianId,
          profileName,
          shouldCustodianSign,
          isLiabilityAccepted,
          hasPurchasedExemptSecurities,
        },
      });

      return updateSchema();
    }

    const profileSdira = {
      custodianId,
      custodianName,
      custodianEinNumber,
      custodianAddress,
    };

    form.setFieldsValue({ profileSdira });

    return updateSchema();
  };

  const inputs: Input[] = [
    ...profileSelectInput,
    ...liabilityInputs,
    {
      name: "sdiraType",
      type: TYPE_RADIO,
      label: "What type of SDIRA do you use for your investment?",
      required: true,
      itemProps: {
        extra: <Disclaimer text={SDIRA_TYPE_EXTRA} />,
      },
      options: [
        {
          value: CUSTODIAN_REVIEWED_SDIRA,
          label: "Custodian-reviewed SDIRA",
        },
        {
          value: CHECKBOOK_CONTROLLED_SDIRA,
          label: "Checkbook-controlled SDIRA",
        },
      ],
      inputProps: {
        onChange: onSdiraTypeChange,
      },
    },
    {
      name: "shouldCustodianSign",
      type: TYPE_BOOLEAN,
      label: "Is your SDIRA custodian required to review your investment or sign?",
      required: true,
      itemProps: {
        rules: [
          {
            message: LABEL_INVALID_SDIRA_TYPE,
            type: "enum",
            enum: [ !isCheckbookControlled ],
          },
        ],
      },
      inputProps: {
        vertical: true,
        hasInvertedItems: isCheckbookControlled,
        onChange: onShouldCustodianSignChange,
      },
    },
    entityNameInput,
    hasEinNumberInput,
    einNumberInput,
    {
      name: "custodianId",
      label: "Custodian",
      operation: listCustodiansOperation,
      extraOptions: CUSTODIANS_EXTRA_OPTIONS,
      required: true,
      placeholder: "Select custodian",
      inputProps: {
        onChange: onCustodianChange,
      },
    },
    {
      name: "custodianName",
      placeholder: "IRA Financial",
      type: isCustodianOtherSelected
        ? TYPE_STRING
        : TYPE_HIDDEN,
    },
    {
      name: "accountName",
      label: "SDIRA Account Name and Account Number",
      required: true,
      placeholder: "IRA Financial FBO John Doe 123456",
      itemProps: {
        extra: getAccountNameExtra(),
        rules: [
          {
            max: ACCOUNT_NAME_MAX_LENGTH,
            message: ERROR_VALIDATION_ACCOUNT_NAME_MESSAGE,
          },
          RULES.ENTITY_NAME,
        ],
      },
      inputProps: {
        count: {
          show: true,
          max: ACCOUNT_NAME_MAX_LENGTH,
        },
        onChange: () => {
          return updateSchema();
        },
      },
    },
    {
      name: "custodianAddress",
      label: "",
      schema: [
        {
          name: "addressCountry",
          type: TYPE_HIDDEN,
        },
        {
          name: "streetAddress",
          label: "Street Address",
          placeholder: "Street address, apt, suite, unit, building, floor, etc",
          required: true,
          inputProps: {
            disabled: isDisabledFields,
          },
        },
        {
          name: "addressLocality",
          label: "City",
          required: true,
          inputProps: {
            disabled: isDisabledFields,
          },
          placeholder: "",
        },
        {
          name: "addressRegion",
          label: "State",
          required: true,
          options: US_STATE_OPTIONS,
          inputProps: {
            disabled: isDisabledFields,
            style: REGION_INPUT_STYLE,
          },
          placeholder: "",
        },
        {
          name: "postalCode",
          label: "ZIP Code",
          type: TYPE_ZIP,
          required: true,
          inputProps: {
            disabled: isDisabledFields,
            style: REGION_INPUT_STYLE,
          },
          placeholder: "",
        },
      ],
    },
    {
      name: "custodianEinNumber",
      type: TYPE_EIN,
      label: "Custodian's EIN",
      required: true,
      inputProps: {
        disabled: isDisabledFields,
      },
      placeholder: "",
    },
  ];

  return inputs;
};

export default getProfileSdiraSchema;
